"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useVideoBuffer = void 0;

var _react = require("react");

// not rocket science, just quick enough to feel fast while spaced enough to not disturb other stuff
const BUFFER_POLL_INETERVAL = 16 * 5;
/**
 * useVideoBuffer reports the last second that was loaded in a video tag, in seconds
 * It's a bit naive by design - You only get the end of the last buffer range, without getting into multiple ranges
 * i.e. for a 18s video which currently has two separate ranges that were loaded:
 * [ ===     =====   ]
 * 0 2 4     10 14  18
 * this hook will yield { bufferedUntil: 14 }, even though there's a portion that wasn't loaded yet in the middle
 *
 *  @param {element} videoTag - an HTML <video> tag
 *  @returns {number} bufferedUntil, the end of the last buffered time range, in seconds
 */

const useVideoBuffer = videoTag => {
  const progressTimer = (0, _react.useRef)(null); // using `progress` + `suspend` events proved to be not reliable enough, so polling it is

  function pollProgress() {
    stopPollingProgress();
    progressTimer.current = setInterval(onProgress, BUFFER_POLL_INETERVAL);
  }

  function stopPollingProgress() {
    clearInterval(progressTimer.current);
  }

  const [bufferedUntil, setBufferedUntil] = (0, _react.useState)(0);
  const latestBufferedUntil = (0, _react.useRef)(bufferedUntil);

  function onProgress() {
    if (videoTag.buffered && videoTag.buffered.length) {
      const lastBufferedEnd = videoTag.buffered.end(videoTag.buffered.length - 1);

      if (lastBufferedEnd !== bufferedUntil) {
        latestBufferedUntil.current = lastBufferedEnd;
        setBufferedUntil(lastBufferedEnd);
        const diffFromEnd = videoTag.duration - lastBufferedEnd; // sometimes the video may report that it loaded *almost* everything, by a tiny margin -
        // We consider that done and clear the interval

        const consideredDone = diffFromEnd / videoTag.duration < 0.01;

        if (consideredDone) {
          stopPollingProgress();
        }
      }
    }
  }

  (0, _react.useEffect)(() => {
    pollProgress();
    return () => {
      stopPollingProgress();
    };
  }, [videoTag.src]);
  return {
    bufferedUntil: latestBufferedUntil.current,
    bufferedUntilPercentage: videoTag.duration ? latestBufferedUntil.current / videoTag.duration * 100 : 0
  };
};

exports.useVideoBuffer = useVideoBuffer;